import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { DocPartyStrip } from "@/modules/docs/DocPartyStrip";
import { RelatedDocLink } from "@/modules/docs/RelatedDocLink";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildSiPrint } from "@/modules/docs/print/doc-print-math";
import { useOrgLetterhead } from "@/modules/docs/print/use-org-letterhead";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import { PayMethodSelect } from "@/modules/core/components/PayMethodSelect";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import { DeskDocActionRail } from "@/modules/shell/DeskDocActionRail";
import { DocActivity } from "@/modules/shell/DocActivity";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import {
  getSalesInvoice,
  postCustomerPayment,
  submitSalesInvoice,
  voidSalesInvoice,
} from "@/shared/api/sales";
import { newClientIdempotencyKey } from "@/shared/client-idempotency";
import { notify } from "@/shared/feedback/notify";

export function SalesInvoiceDetailPage() {
  const { companySlug, invoiceId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const { letterhead } = useOrgLetterhead(companySlug);
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getSalesInvoice>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethodId, setPayMethodId] = useState<string | null>(null);

  async function reload() {
    if (!companySlug || !invoiceId) return;
    const d = await getSalesInvoice(companySlug, invoiceId);
    setData(d);
    setPayAmount(d.invoice.outstanding);
  }

  useEffect(() => {
    let cancelled = false;
    if (!companySlug || !invoiceId) return;
    getSalesInvoice(companySlug, invoiceId)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setPayAmount(d.invoice.outstanding);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, invoiceId, navigate]);

  async function onCollect() {
    if (!companySlug || !invoiceId || !data) return;
    const inv = data.invoice;
    if (!inv.salesOrderId) {
      setError("Standalone invoice collect is not supported yet");
      return;
    }
    if (!(Number(payAmount) > 0)) {
      setError("Enter a positive amount");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await postCustomerPayment(companySlug, {
        customerId: inv.customerId,
        salesOrderId: inv.salesOrderId,
        salesInvoiceId: inv.id,
        amount: payAmount,
        payMethodId: payMethodId ?? undefined,
        idempotencyKey: newClientIdempotencyKey("si-pay"),
      });
      notify.success("Payment recorded");
      navigate(`/c/${companySlug}/sales/payments/${result.payment.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Collect failed");
    } finally {
      setBusy(false);
    }
  }

  useRecordRecentDoc({
    companySlug,
    kind: "salesInvoice",
    id: data?.invoice.id,
    label: data?.invoice.number,
    path:
      companySlug && data?.invoice.id
        ? `/c/${companySlug}/sales/invoices/${data.invoice.id}`
        : undefined,
    hint: data?.invoice.customerName,
  });

  async function onSubmit() {
    if (!companySlug || !invoiceId) return;
    const ok = await confirmUi({
      title: "Submit this invoice?",
      description: "It becomes open (posted) and can receive payments.",
      confirmLabel: "Submit",
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await submitSalesInvoice(companySlug, invoiceId);
      notify.success("Invoice submitted");
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Submit failed");
    } finally {
      setBusy(false);
    }
  }

  async function onVoid() {
    if (!companySlug || !invoiceId) return;
    const ok = await confirmUi({
      title: "Void this invoice?",
      description:
        "Posted payments on this invoice must be cleared first. The number is kept.",
      confirmLabel: "Void",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await voidSalesInvoice(companySlug, invoiceId);
      notify.success("Invoice voided");
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Void failed");
    } finally {
      setBusy(false);
    }
  }

  if (!companySlug || !invoiceId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading invoice…</Alert>
        )}
      </PageFrame>
    );
  }

  const { invoice: inv } = data;
  const printData = buildSiPrint({
    number: inv.number,
    customerName: inv.customerName,
    lifecycle: inv.lifecycle,
    docDate: inv.docDate,
    paymentDueOn: inv.paymentDueOn,
    discountPct: inv.discountPct,
    netTotal: inv.netTotal,
    taxTotal: inv.taxTotal,
    grossTotal: inv.grossTotal,
    customerRef: inv.customerRef,
    salesOrderNumber: inv.salesOrderNumber,
    primaryPhone: inv.customerPrimaryPhone,
    primaryEmail: inv.customerPrimaryEmail,
    primaryAddress: inv.customerPrimaryAddress,
    lines: inv.lines.map((l) => ({
      productName: l.productName,
      qty: l.qty,
      unitPrice: l.unitPrice,
      lineGross: l.lineGross,
    })),
    issuedAt: inv.submittedAt ?? inv.createdAt,
  });

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} letterhead={letterhead} />
      </div>
      <div className="no-print">
        <div className="mb-3">
          <DeskDocBackLink
            listBasePath={`/c/${companySlug}/sales/invoices`}
            listLabel="Sales invoices"
          />
        </div>
        <PageHeader
          eyebrow="Sales invoice"
          title={inv.number}
          description={[inv.customerName, inv.docDate]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => printDocSlip()}
              >
                Print
              </Button>
              {inv.lifecycle === "draft" && (
                <Button
                  type="button"
                  size="sm"
                  disabled={busy}
                  onClick={() => void onSubmit()}
                >
                  Submit
                </Button>
              )}
              {inv.lifecycle !== "void" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void onVoid()}
                >
                  Void
                </Button>
              )}
            </div>
          }
        />
        <div className="mt-2">
          <LifecycleStatus value={inv.lifecycle} showLabel />
        </div>
        <DocPartyStrip
          companySlug={companySlug}
          partyId={inv.customerId}
          partyName={inv.customerName}
          roleLabel="Customer"
          primaryPhone={inv.customerPrimaryPhone}
          primaryEmail={inv.customerPrimaryEmail}
          primaryAddress={inv.customerPrimaryAddress}
          partyNotes={inv.customerNotes}
          docNotes={inv.notes}
        />
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="sales_invoice"
          docId={invoiceId}
          linkPath={`/c/${companySlug}/sales/invoices/${invoiceId}`}
        />
        {inv.lifecycle === "open" &&
        inv.salesOrderId &&
        Number(inv.outstanding) > 0 ? (
          <DeskDocActionRail className="mt-4">
            <Input
              className="w-28 font-mono"
              inputMode="decimal"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              aria-label="Collect amount"
            />
            <PayMethodSelect
              companySlug={companySlug}
              value={payMethodId}
              onChange={setPayMethodId}
            />
            <Button
              type="button"
              size="sm"
              disabled={busy || !payAmount.trim()}
              onClick={() => void onCollect()}
            >
              Collect
            </Button>
          </DeskDocActionRail>
        ) : null}
      </div>

      <FieldGroup title="Summary" className="mt-6" level="sub">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          {inv.salesOrderId && inv.salesOrderNumber ? (
            <>
              <dt className="text-[var(--color-muted)]">Sales order</dt>
              <dd className="m-0">
                <RelatedDocLink
                  companySlug={companySlug}
                  kind="sales_order"
                  docId={inv.salesOrderId}
                >
                  {inv.salesOrderNumber}
                </RelatedDocLink>
              </dd>
            </>
          ) : null}
          <dt className="text-[var(--color-muted)]">Doc date</dt>
          <dd className="m-0">{inv.docDate}</dd>
          {inv.paymentDueOn ? (
            <>
              <dt className="text-[var(--color-muted)]">Payment due</dt>
              <dd className="m-0">{inv.paymentDueOn}</dd>
            </>
          ) : null}
        </dl>
      </FieldGroup>

      <FieldGroup title="Totals" className="mt-4" level="sub">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-[var(--color-muted)]">Net</dt>
          <dd className="m-0 font-mono tabular-nums">
            {moneyDisplay(inv.netTotal)}
          </dd>
          <dt className="text-[var(--color-muted)]">Tax</dt>
          <dd className="m-0 font-mono tabular-nums">
            {moneyDisplay(inv.taxTotal)}
          </dd>
          <dt className="text-[var(--color-muted)]">Total</dt>
          <dd className="m-0 font-mono font-medium tabular-nums">
            {moneyDisplay(inv.grossTotal)}
          </dd>
          <dt className="text-[var(--color-muted)]">Paid</dt>
          <dd className="m-0 font-mono tabular-nums">
            {moneyDisplay(inv.amountPaid)}
          </dd>
          <dt className="text-[var(--color-muted)]">Outstanding</dt>
          <dd className="m-0 font-mono font-medium tabular-nums">
            {moneyDisplay(inv.outstanding)}
          </dd>
        </dl>
      </FieldGroup>

      <AdvancedSection className="no-print" subtitle="tax · discount · refs">
        <FieldGroup title="Tax" level="sub">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-[var(--color-muted)]">Rate</dt>
            <dd className="m-0 font-mono">
              {inv.taxRatePct}% {inv.taxPriceMode}
            </dd>
          </dl>
        </FieldGroup>
        <FieldGroup title="Discount" level="sub">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-[var(--color-muted)]">Invoice</dt>
            <dd className="m-0 font-mono">{inv.discountPct}%</dd>
          </dl>
        </FieldGroup>
        <FieldGroup title="Refs" level="sub">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {inv.customerRef ? (
              <>
                <dt className="text-[var(--color-muted)]">Customer ref</dt>
                <dd className="m-0">{inv.customerRef}</dd>
              </>
            ) : (
              <dd className="m-0 text-[var(--color-muted)]">None set</dd>
            )}
          </dl>
        </FieldGroup>
      </AdvancedSection>

      <section className="mt-6 border-t border-[var(--color-border)]/90 pt-4">
        <h2 className="m-0 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight">
          Lines
        </h2>
        {inv.lines.length === 0 ? (
          <div className="mt-3">
            <EmptyState title="No lines" description="This invoice has no lines." />
          </div>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[28rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-muted)]">
                  <th className="py-2 pr-3 font-medium">Product</th>
                  <th className="py-2 pr-3 font-medium text-right">Qty</th>
                  <th className="py-2 pr-3 font-medium text-right">Price</th>
                  <th className="py-2 font-medium text-right">Gross</th>
                </tr>
              </thead>
              <tbody>
                {inv.lines.map((l) => (
                  <tr
                    key={l.id}
                    className="border-b border-[var(--color-border)]/60"
                  >
                    <td className="py-2 pr-3">
                      {l.productName}
                      {l.description ? (
                        <div className="text-xs text-[var(--color-muted)]">
                          {l.description}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">
                      {l.qty}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">
                      {moneyDisplay(l.unitPrice)}
                    </td>
                    <td className="py-2 text-right font-mono tabular-nums">
                      {moneyDisplay(l.lineGross)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="sales_invoice"
        docId={invoiceId}
        linkPath={`/c/${companySlug}/sales/invoices/${invoiceId}`}
      />
    </PageFrame>
  );
}
